import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { getCoinbaseCorrections, CoinbaseCorrection } from '../../api/tax';
import { useTaxYearStore } from '../../stores/useTaxYearStore';
import { useAutoRefreshTrigger } from '../../stores/useGlobalAutoRefreshStore';
import { formatOrderId, formatTimestamp } from './taxReportingUtils';
import DataTable, { DataTableFilter } from '../shared/DataTable';

// Read-only server-paginated audit log of Coinbase transaction corrections.
// See CoinbaseTransactionsView.tsx for the canonical server-paginated idiom.

export const CoinbaseCorrectionsView: React.FC = () => {
  const taxYear = useTaxYearStore((s) => s.selectedYear);
  const refreshTrigger = useAutoRefreshTrigger('tax-reporting');

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [coinbaseOrderId, setCoinbaseOrderId] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'created_at', desc: true }]);
  const sortBy = sorting[0]?.id;
  const sortDir: 'asc' | 'desc' | undefined = sorting[0] ? (sorting[0].desc ? 'desc' : 'asc') : undefined;

  const onTextFilterChange =
    (setter: (v: string) => void) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setter(e.target.value);
      setPageIndex(0);
    };

  const params: Record<string, string | number | undefined> = {
    taxYear,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    coinbaseOrderId: coinbaseOrderId || undefined,
    sortBy,
    sortDir,
    page: pageIndex + 1,
    pageSize,
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: [
      'tax',
      'coinbase-corrections',
      taxYear,
      startDate,
      endDate,
      coinbaseOrderId,
      sortBy,
      sortDir,
      pageIndex,
      pageSize,
      refreshTrigger,
    ],
    queryFn: () => getCoinbaseCorrections(params),
  });

  const items = data?.items ?? [];
  const total = data?.metadata.total ?? 0;
  const pageCount = data ? Math.max(1, Math.ceil(total / pageSize)) : -1;

  const columns = useMemo<ColumnDef<CoinbaseCorrection>[]>(
    () => [
      { accessorKey: 'auditId', header: 'Audit ID' },
      {
        accessorKey: 'entityId',
        header: 'Order ID',
        cell: (info) => (
          <span className="order-id-cell">{formatOrderId(info.getValue() as string | null)}</span>
        ),
        enableSorting: false,
      },
      { accessorKey: 'action', header: 'Action' },
      { accessorKey: 'fieldName', header: 'Field' },
      { accessorKey: 'oldValue', header: 'Old Value' },
      { accessorKey: 'newValue', header: 'New Value' },
      { accessorKey: 'reason', header: 'Reason' },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: (info) => (
          <span className="date-cell">{formatTimestamp(info.getValue() as string)}</span>
        ),
      },
    ],
    []
  );

  // Custom filter row using DataTable's filters slot conventions. Date and
  // text inputs render as <input> elements rather than selects, so we render
  // a small wrapper above the DataTable for them. To keep the chrome
  // consistent we still pass the canonical CSS classes via a plain element.
  const filters: DataTableFilter[] = [];

  return (
    <div aria-label="Coinbase Corrections view">
      <div className="table-filters">
        <div className="filter-group">
          <label htmlFor="cc-start-date">Start Date:</label>
          <input
            id="cc-start-date"
            type="date"
            value={startDate}
            onChange={onTextFilterChange(setStartDate)}
          />
        </div>
        <div className="filter-group">
          <label htmlFor="cc-end-date">End Date:</label>
          <input
            id="cc-end-date"
            type="date"
            value={endDate}
            onChange={onTextFilterChange(setEndDate)}
          />
        </div>
        <div className="filter-group">
          <label htmlFor="cc-order-id">Order ID:</label>
          <input
            id="cc-order-id"
            type="text"
            placeholder="substring match"
            value={coinbaseOrderId}
            onChange={onTextFilterChange(setCoinbaseOrderId)}
          />
        </div>
      </div>
      <DataTable<CoinbaseCorrection>
        data={items}
        columns={columns}
        serverSorting={{
          state: sorting,
          onChange: (s) => {
            setSorting(s);
            setPageIndex(0);
          },
        }}
        filters={filters}
        loadingMessage="Loading corrections…"
        isLoading={isLoading}
        isError={isError}
        errorMessage={error instanceof Error ? error.message : 'Failed to load corrections'}
        emptyMessage="No Coinbase corrections for these filters."
        serverPagination={{
          pageIndex,
          pageSize,
          pageCount,
          totalRows: total,
          onPageIndexChange: setPageIndex,
          onPageSizeChange: (size) => {
            setPageSize(size);
            setPageIndex(0);
          },
        }}
      />
    </div>
  );
};

export default CoinbaseCorrectionsView;
